import React, { memo, useCallback, useEffect, useState, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { AspectRatio, Box, Flex, Image, Text, useBreakpointValue, Container, Stack } from "@chakra-ui/react"

import { LookBookProductCard } from "@app/components/Product/LookBook/LookBookProductCard"
import { useShopify } from "@app/hooks/useShopify"
import { useImage } from "@app/hooks/useImage"
import type { ProductProps } from "@root/types/global"

import "swiper/css"

type Props = {
  lookbook: GatsbyTypes.SanityLookBookProduct
}

export const LookBookProduct: React.FC<Props> = memo(({ lookbook }) => {
  const { product } = useStaticQuery<GatsbyTypes.LookBookProductQuery>(graphql`
    query LookBookProduct {
      product: sanityTemplateProduct {
        additionalLookbookLabel
      }
    }
  `)
  const { getHandle, getProducts } = useShopify()

  const { getResponsiveImage } = useImage()

  const [items, setItems] = useState<Array<ProductProps> | null>(null)
  const image = useMemo(() => getResponsiveImage(lookbook?.image), [getResponsiveImage, lookbook?.image])
  const ratio = useBreakpointValue({ base: 0.535, md: 0.6 })

  const fetchItems = useCallback(async () => {
    const items = await getProducts({ firstImages: 2, firstVariants: 1, handles: lookbook?.products?.map(item => getHandle(item)) })

    if (items) setItems(items)
  }, [lookbook, getHandle, getProducts])

  // intentionally only run once at first render
  useEffect(() => {
    if (!items && lookbook?.products?.length) fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasFullItem = items?.length === 4

  if (!lookbook?.products || !items) return null

  return (
    <Container as="section" mb={0} px={[6.5, 12.5]} pt={[10, 16]} pb={3} position="relative">
      <Flex alignItems="center" flexWrap={{ base: "wrap", md: "nowrap" }} overflowX="hidden" overflowY="initial" marginBottom={6} gap={12}>
        <Text fontSize={["2xl", "3xl"]} as="h2" lineHeight={"base"} color="grey.text" fontWeight="500" whiteSpace="nowrap">
          {product?.additionalLookbookLabel}
        </Text>
        <Box as="div" display={{ base: "none", md: "block" }} width="100%" height="1px" background="grey.lake" />
      </Flex>

      <Flex alignItems={{ base: "stretch", md: "flex-start" }} gap={{ base: 9, md: 12 }} flexDir={{ base: "column", md: "row" }} flex="1">
        <Box
          display={{ base: lookbook?.hideMobileImage ? "none" : "block", sm: "block" }}
          width="100%"
          maxWidth={{ base: "100%", md: "500px" }}
        >
          <AspectRatio ratio={ratio}>
            <Image {...image} />
          </AspectRatio>
        </Box>
        <Stack gap={4} flexDir="column" flex="1">
          {items.map((lookbook, index) => (
            <LookBookProductCard item={lookbook} pagePosition={index} hasFullItem={hasFullItem} />
          ))}
        </Stack>
      </Flex>
    </Container>
  )
})
